import ImageFileUploader from 'components/FileUploader/Image'
import Translation from 'components/Translation'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { default_primary_color, useAuth } from 'context/AuthContext'
import { MouseEvent, useEffect, useState } from 'react'
import { HexColorInput, HexColorPicker } from 'react-colorful'
import { SubmitHandler, useForm } from 'react-hook-form'
import { classNames } from 'utils/dom-helpers'
import {
    deleteApiRequest,
    postApiRequest,
    putApiRequest,
} from 'utils/fetch-helper'
import DropdownComponent from '../../DropdownSelectors'
import { DropPosition } from '../../DropdownSelectors/common'
import { GeneralFormValues, FormErrors } from './types'
import { useTenantContext } from 'context/TenantContext'
import GenericError from 'components/GenericError/GenericError'
import Editor from './Editor'
import { ReactElement } from 'react'
import DialogModal from '../Dialog'
import { getTranslation } from '../../../utils/language-helper'
import CustomSwitch from '../../appointmentDrawer/CustomSwitch'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import PreviewBookingPage from './PreviewBookingPage'
import getConfig from 'next/config'
import { CloseIcon, CopyIcon, EditIcon, ImageIcon, ViewIcon } from 'utils/custom-icons'

export const ENTRY_WITH_CATEGORY = 'ENTRY_WITH_CATEGORY'
export const INTRO_MESSAGE_KEY = 'INTRO_MESSGAE'
export const SHOW_STAFF_KEY = 'SHOW_STAFF'
export const SHOW_INTRO_BORDER = 'SHOW_INTRO_BORDER'
export const SHOW_FREE_BOOKING = 'SHOW_FREE_BOOKING'
export const SHOW_MEMBERSHIPS = 'SHOW_MEMBERSHIPS'

export default function GeneralForm({ from_settings }: { from_settings?: boolean }) {
    const { BOOKING_HOST } = getConfig().publicRuntimeConfig
    const { setGlobalError } = useAppErrorApiContext()
    const {
        setGlobalLoading,
        translations,
    } = useAppContext()
    const { CustomBookingPageModal, GlobalDialog } = useAuth()
    const {
        tenant,
        db_settings,
        getDbSettings,
        tenant_subdomains,
        getTenantSubdomains,
        entry_point,
        allow_entry_by_category,
        show_services_include,
        primaryColor,
        locationBg
    } = useTenantContext()
    const { attributes, setAttributes } = CustomBookingPageModal
    const [is_dirty, setIsDirty] = useState(false)
    const form_errors: FormErrors =
        (attributes && (attributes.form_errors as unknown as FormErrors)) ||
        {}
    const [introMessage, setInroMessage] = useState('')
    const [show_upload_view, setShowUploadView] = useState(true)
    const [show_mouse_hand, setShowMouseHand] = useState(false)
    const [location_image, setLocationImage] = useState('')
    const [booking_url, setBookingUrl] = useState('')
    const [displayBorder, setDisplayBorder] = useState(false)

    const [error, setError] = useState<IError>(DEFAULT_ERROR_STATE)
    const [prompt_message, toggleDialog] = useState<string | ReactElement>(
        ''
    )
    const [displayEditor, setDisplayEditor] = useState(false)

    const {
        handleSubmit,
        formState: { errors },
    } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        if (attributes?.image_url && !location_image) {
            setLocationImage(attributes?.image_url as string)
            setShowUploadView(false)
        }
    }, [attributes])

    useEffect(() => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === INTRO_MESSAGE_KEY
        )
        if (found?.value) {
            setInroMessage(found?.value)
            setDisplayEditor(true)
        }

        handleDisplayIntroBorder()
        setAttributes({
            ...attributes,
            image_url: db_settings?.content?.find(
                (item: any) => item.name === 'location_bg'
            )?.value,
            primary_color:
                db_settings?.content?.find(
                    (item: any) => item.name === 'primary_color'
                )?.value || default_primary_color,
        })
        if (attributes?.refetch) {
            toggleDialog(
                getTranslation(
                    'common.customize_booking_page_success',
                    translations
                )
            )
        }
    }, [db_settings])

    useEffect(() => {
        if (tenant_subdomains && tenant_subdomains.length) {
            if (tenant_subdomains[0]?.subdomain) {
                const url = tenant_subdomains[0].subdomain
                setBookingUrl(url)
            }
        }
    }, [tenant_subdomains])

    const handlePostDbSetting = async (payload: any) => {
        await postApiRequest('/v1/settings', payload)
    }
    const handlePutDbSetting = async (payload: any) => {
        await putApiRequest(`/v1/settings/${payload?.id}`, payload)
    }

    const onSubmit: SubmitHandler<GeneralFormValues> = async () => {
        try {
            handleIntroMessgaeUpdate()
            handleShowIntroBorder()

            setGlobalLoading(true)
            const primary_color = 'primary_color'
            const location_bg = 'location_bg'
            const primary_color_data = db_settings?.content?.find(
                (item: any) => item.name === primary_color
            )?.id
            const location_bg_data = db_settings?.content?.find(
                (item: any) => item.name === location_bg
            )?.id

            const payload_color = {
                category: 'BUSINESS',
                id: !primary_color_data ? '' : primary_color_data,
                name: primary_color,
                value: attributes?.primary_color,
                tenantId: tenant?.id,
            }

            const payload_location_bg = {
                category: 'BUSINESS',
                id: !location_bg_data ? '' : location_bg_data,
                name: location_bg,
                value: attributes?.image_url,
                tenantId: tenant?.id,
            }

            const color_api = payload_color.id
                ? await putApiRequest(
                    `/v1/settings/${primary_color_data}`,
                    payload_color
                )
                : await postApiRequest('/v1/settings', payload_color)

            let bg_api
            if (payload_location_bg.value) {
                bg_api = payload_location_bg.id
                    ? await putApiRequest(
                        `/v1/settings/${location_bg_data}`,
                        payload_location_bg
                    )
                    : await postApiRequest(
                        '/v1/settings',
                        payload_location_bg
                    )
            } else {
                bg_api = payload_location_bg.id
                    ? await deleteApiRequest(
                        `/v1/settings/${location_bg_data}`
                    )
                    : ''
            }

            if (attributes?.booking_url) {
                const tenant_domain_payload = {
                    subdomain: attributes?.booking_url.toString().toLocaleLowerCase(),
                    tenantId: tenant?.id,
                }
                let subdomain_api
                if (tenant_subdomains.length == 1) {
                    subdomain_api = await postApiRequest(
                        '/v1/tenant-domains',
                        tenant_domain_payload
                    )
                } else {
                    if (
                        tenant_domain_payload.subdomain ===
                        tenant_subdomains[1]?.subdomain
                    ) {
                        subdomain_api = await deleteApiRequest(
                            `/v1/tenant-domains/${tenant_subdomains[0]?.id}`
                        )
                    } else {
                        subdomain_api = await putApiRequest(
                            `/v1/tenant-domains/${tenant_subdomains[0]?.id}`,
                            tenant_domain_payload
                        )
                    }
                }
                if (subdomain_api && subdomain_api.status === 400) {
                    throw new Error(subdomain_api?.message)
                }
            }

            if (color_api.id || bg_api.id) {
                setAttributes({
                    ...attributes,
                    refetch: true,
                })
            }

            getDbSettings()
            getTenantSubdomains()
        } catch (err) {
            setGlobalError(err)
        } finally {
            setGlobalLoading(false)
        }
    }

    const handleDisplayIntroBorder = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_INTRO_BORDER
        )
        if (found) {
            setDisplayBorder(found?.value == 'true' ? true : false)
        }
    }

    const handleIntroMessgaeUpdate = () => {
        try {
            const data = db_settings?.content?.find(
                (item: any) => item.name === INTRO_MESSAGE_KEY
            )
            let payload = {
                category: 'BUSINESS',
                id: !data ? '' : data?.id,
                name: INTRO_MESSAGE_KEY,
                value: introMessage,
                tenantId: tenant?.id,
            }
            let res: any = {}
            if (data?.id) {
                res = handlePutDbSetting(payload)
            } else if (introMessage?.length > 0) {
                res = handlePostDbSetting(payload)
            }
            if (!res?.ok) {
                throw res
            }
        } catch (error) {
            setError({ message: error?.message as string, tag: error?.tag })
        }
    }

    const handleShowIntroBorder = () => {
        const data = db_settings?.content?.find(
            (item: any) => item.name === SHOW_INTRO_BORDER
        )

        let payload = {
            category: 'BUSINESS',
            id: !data ? '' : data?.id,
            name: SHOW_INTRO_BORDER,
            value: JSON.stringify(displayBorder),
            tenantId: tenant?.id,
        }

        const isPut = !data ? false : true

        let res: any = {}
        switch (isPut) {
            case true:
                res = handlePutDbSetting(payload)
                break
            case false:
                res = handlePostDbSetting(payload)
                break
            default:
                null
        }
        if (!res.ok)
            setError({ message: res?.message as string, tag: res?.tag })
    }

    function handleCloseModal(e: MouseEvent<HTMLButtonElement>) {
        CustomBookingPageModal.setAttributes({})
        CustomBookingPageModal.close()
    }

    function handleViewLive() {
        window.open(getBookingUrl(), '_blank', 'noopener,noreferrer')
    }

    function getBookingUrl() {
        const subdomain = document
            .getElementById('booking-url')
            ?.getAttribute('value')
        return `https://${subdomain}.${BOOKING_HOST}/booking`
    }

    function handleCopyUrl() {
        navigator.clipboard.writeText(getBookingUrl())
        GlobalDialog.setAttributes({
            message: 'Copied to clipboard.',
            className: 'w-96 overflow-hidden',
        })
        GlobalDialog.open()
        setTimeout(() => {
            GlobalDialog.close()
        }, 1200)
    }

    function handleOnAdd(image_url: string) {
        setLocationImage(image_url)
        setShowUploadView(false)
        setIsDirty(true)
    }

    function handleUploadStatus(status: string) {
        if (status === 'done' && attributes?.image_url !== '') {
            setIsDirty(true)
        } else {
            setIsDirty(false)
        }
    }

    function UrlPreviewComponent(props: any) {
        return (
            <div className="grid grid-flow-col">
                <div className="col">
                    <img
                        src={props.image_url}
                        className="h-32 rounded-lg"
                        alt={props.image_alt}
                    />
                </div>
                <div className="col-span-4">
                    <p className="text-lg my-2">{props.image_alt}</p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowUploadView(true)}
                            className="flex gap-3 items-center bg-primary-light text-white px-2 py-1 rounded-lg text-sm justify-center"
                        >
                            <ImageIcon color='white'/>
                            <span>{translations['replace_photo']}</span>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    function showUploadView() {
        if (!show_upload_view) {
            return (
                <UrlPreviewComponent
                    image_url={location_image as string}
                    image_alt={translations['booking_background_img']}
                />
            )
        } else {
            return (
                <ImageFileUploader
                    uploadStatus={handleUploadStatus}
                    tenant_id={tenant?.id}
                    onAdd={handleOnAdd}
                    accept=".jpeg, .jpg, .gif, .png"
                    form_context={{
                        attributes: attributes || {},
                        setAttributes: setAttributes as unknown as (
                            data: Record<string, string | File>
                        ) => void,
                    }}
                    addClassNames={{
                        dropzone:
                            'bg-transparent p-0 overflow-hidden h-64 border-0',
                        inputLabel:
                            'text-gray-400 font-medium text-md flex-grow',
                        submitButtonContainer: 'mt-3',
                        submitButton:
                            'flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-md font-serif font-light justify-center',
                        labelStyle: '',
                    }}
                />
            )
        }
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)} className="flex">
            {!!error?.message?.length && (
                <GenericError
                    error_message={error.message}
                    onCloseModal={() => setError(DEFAULT_ERROR_STATE)}
                    tag={error?.tag}
                />
            )}
            <DialogModal
                prompt_message={prompt_message}
                onCloseModal={() => {
                    toggleDialog('')
                    setIsDirty(false)
                    delete attributes?.refetch
                    CustomBookingPageModal.close()
                }}
            />
            <div
                id="booking-general-form"
                className="flex flex-row w-full h-full overflow-y-auto"
            >
                <div className='flex flex-col w-1/2 p-5 h-full'>
                    <div className="flex justify-between w-full">
                        <Translation
                            render_as="span"
                            className="text-primary text-lg font-medium font-display pb-3"
                            content_key="basic_info_panel_title"
                        />
                        <button
                            type="button"
                            className="border flex items-center flex-row border-primary-light rounded-lg py-1 px-2"
                            onClick={() => {
                                handleViewLive()
                            }}
                        >
                            <ViewIcon/>
                            <Translation
                                render_as="span"
                                className="text-primary-light mx-1"
                                content_key="view_live"
                                translations={translations}
                            />
                            <i className="feather feather-external-link text-primary-light" />
                        </button>
                    </div>
                    <fieldset className="lg:pb-4">
                        <Translation
                            render_as="label"
                            htmlFor="booking-url"
                            className="block text-sm"
                            content_key="booking_url"
                        />
                        <div className="relative flex items-center text-md">
                            <input
                                type="text"
                                id="booking-url"
                                className="px-2 py-1 mt-1 focus:ring-primary-light focus:border-primary-light block w-48 shadow-sm border-gray-300 rounded-md mr-2"
                                value={booking_url}
                                onChange={(e) => {
                                    setIsDirty(true)
                                    setAttributes({
                                        ...attributes,
                                        booking_url: e.target.value,
                                    })
                                    setBookingUrl(e.target.value)
                                }}
                            />
                            <strong>.{BOOKING_HOST}/booking</strong>
                            <span 
                            className="absolute text-lg right-0 cursor-pointer hover:drop-shadow-xl"
                            onClick={() => {
                                    handleCopyUrl()
                                }}>
                                <CopyIcon color='#5E9FBD' style={{width: '20px', height: '20px'}}/>
                            </span>
                        </div>
                    </fieldset>
                    <div className='flex flex-col bg-[#E6E7E8] w-full p-4 rounded-lg mb-5 inline-block'>
                        <Translation
                            render_as='span'
                            content_key='settings.custom_booking_app'
                            className='text-base text-medium text-primary-light mb-3 font-semibold'
                        />
                        <Translation
                            render_as='p'
                            content_key='settings.install_custom_booking_app'
                            className='text-base text-sm text-primary-default mb-3'
                        />
                        <div className='flex flex-row gap-1'>
                            <Translation
                                render_as='span'
                                content_key='settings.custom_booking_app_see_this'
                                className='text-sm text-[450] text-zinc-800'
                            />
                            <span
                                className={classNames("underline text-blue-600 focus:cursor-pointer",
                                    show_mouse_hand ? "cursor-pointer" : ""
                                )}
                                onMouseEnter={() => {
                                    setShowMouseHand(true)
                                }}
                                onMouseLeave={() => {
                                    setShowMouseHand(false)
                                }}
                                onClick={() => {
                                    window.open(
                                        `http://help.aot.plus/article/how-to-install-your-branded-app-with-aot`,
                                        '_blank',
                                        'noopener,noreferrer'
                                    )
                                }}>
                                {getTranslation(
                                    'settings.custom_booking_app_link',
                                    translations
                                )}
                            </span>
                            <Translation
                                render_as='span'
                                content_key='settings.custom_booking_more_details'
                                className='text-sm text-[450] text-zinc-800'
                            />
                        </div>


                    </div>
                    <fieldset className="lg:pb-3">
                        <label
                            htmlFor="primary-color"
                            className={classNames(
                                'block text-lg',
                                form_errors.primary_color
                                    ? 'text-red-700'
                                    : ''
                            )}
                        >
                            <Translation content_key='common.primary_color' />
                        </label>

                        <div className="flex gap-3 items-center border border-gray-300 px-2 rounded-lg w-full">
                            <i
                                className="w-6 h-6 rounded-full"
                                style={{
                                    backgroundColor:
                                        attributes?.primary_color as string,
                                }}
                            />

                            <HexColorInput
                                id="primary-color"
                                className="focus:outline-none font-mono flex-1 py-3"
                                color={attributes?.primary_color as string}
                                onChange={(primary_color) => {
                                    setIsDirty(true)
                                    setAttributes({
                                        ...attributes,
                                        primary_color,
                                    })
                                }}
                            />

                            <DropdownComponent
                                label={
                                    <span className="bg-primary bg-opacity-10 hover:bg-opacity-5 text-primary rounded px-2 flex flex-row items-center gap-2 text-sm py-1">
                                        <EditIcon style={{width: '12px', height: '12px'}}/>{' '}
                                        Pick color
                                    </span>
                                }
                                dropboxClassname="p-1 border border-gray-200 rounded-lg"
                                className="p-0"
                                position={DropPosition.TOP_RIGHT}
                                style={{}}
                                items={[
                                    {
                                        input_component: (
                                            <HexColorPicker
                                                onChange={(
                                                    primary_color
                                                ) => {
                                                    setIsDirty(true)
                                                    setAttributes({
                                                        ...attributes,
                                                        primary_color,
                                                    })
                                                }}
                                                color={
                                                    (attributes &&
                                                        (attributes.primary_color as string)) ||
                                                    default_primary_color
                                                }
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </fieldset>
                    <fieldset className="pb-3 lg:pb-0 lg:w-full gap-2">
                        <Translation
                            render_as="label"
                            htmlFor="background-img"
                            className={classNames(
                                'block text-lg',
                                errors.background_image?.type
                                    ? 'text-red-700'
                                    : ''
                            )}
                            content_key="background_image"
                        />
                        {showUploadView()}
                    </fieldset>
                    <fieldset className="lg:pb-3 pt-3">
                        {displayEditor ? (
                            <>
                                <label className="flex justify-between mb-3 text-lg">
                                    <span>
                                        <Translation
                                            content_key={
                                                'intro_message'
                                            }
                                        />{' '}
                                    </span>
                                    <span
                                        className="cursor-pointer"
                                        onClick={() => {
                                            setDisplayEditor(false)
                                            setInroMessage('')
                                            setIsDirty(true)
                                        }}
                                    >
                                        <CloseIcon style={{width: '20px', height: '20px'}}/>
                                    </span>
                                </label>
                                <Editor
                                    introMessage={introMessage}
                                    setIntroMessage={setInroMessage}
                                    setIsDirty={setIsDirty}
                                />
                            </>
                        ) : (
                            <div
                                onClick={() => setDisplayEditor(true)}
                                className="flex items-center"
                            >
                                <i className="feather-plus text-primary-light text-lg mr-2" />
                                <div className="text-primary-light font-sans text-base cursor-pointer">
                                    <Translation
                                        render_as="div"
                                        content_key="add_intro_message"
                                    />
                                </div>
                            </div>
                        )}
                    </fieldset>

                    <fieldset className="flex items-center">
                        <CustomSwitch
                            label={
                                <Translation content_key="Show border around introductory message" />
                            }
                            labelStyles={{}}
                            value={displayBorder}
                            onChangeValue={(val) => {
                                setDisplayBorder(val)
                                setIsDirty(true)
                            }}
                        />
                    </fieldset>
                    <div className="flex justify-end pt-12">
                        <button
                            type="button"
                            className={classNames(
                                'border border-gray-300 rounded-lg py-3 inline-block mr-3 px-2 py-1',
                                from_settings ? 'hidden' : 'visible'
                            )}
                            onClick={(e) => {
                                handleCloseModal(e)
                            }}
                        >
                            <Translation
                                content_key="cancel"
                                translations={translations}
                            />
                        </button>
                        <button
                            type="submit"
                            disabled={!is_dirty}
                            className='group relative flex justify-center py-1 px-2 border border-transparent rounded-md text-white focus:outline-none bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light disabled:opacity-60 disabled:cursor-not-allowed'
                        >
                            <Translation
                                content_key="save"
                            />
                        </button>
                    </div>
                </div>
                <div
                    className='hidden lg:flex flex-col w-1/2 bg-[#F1F3F4] h-full p-5'
                    style={{
                        boxShadow: 'inset 2px 0px 2px rgba(0, 0, 0, 0.05)'
                    }}
                >
                    <PreviewBookingPage
                        show_intro_message={displayEditor}
                        intro_message={introMessage}
                        show_intro_border={displayBorder}
                        entry_point={entry_point}
                        allow_entry_by_category={allow_entry_by_category}
                        show_services_include={show_services_include.value as boolean}
                        primary_color={attributes?.primary_color as string || primaryColor}
                        image_url={attributes?.image_url as string || locationBg}
                    />
                </div>
            </div>
        </form>
    )
}